// const fmsGlobal = getFmsGlobal();

import {JobNames} from "../../../../../framework.core/JobNames";
import React, {MutableRefObject, useContext, useEffect} from "react";
import ApplicationContext, {IApplicationContext} from "../../../../../ApplicationContext";
import {useTypedFastStorage} from "../../../../../hooks/useFastStorage";
import _ from "lodash";
import TextBox from "../../../../../ui/TextBox";
import {getDecimal} from "../../../../../util";
import {ApplicableTableSettings} from "../../../../../tableSettings";
import {bankEdiTable} from "../../../../reports/tableSettings";
import InlineInputControlRow from "../../../../../ui/layout/InlineInputControlRow";
import EditableTable, { CellData, TableCellTypes } from "../../../../../ui/tables/editing/EditableTable";
import SecondaryButton from "../../../../../ui/SecondaryButton";
import Date from "../../../../../ui/Date";
import {IGeneralWithList} from "../../../../../ifmsTypes";
import BankDropDown from "../../../../../uiControls/BankDropDown";
import PrimaryButton from "../../../../../ui/PrimaryButton";
import Float from "../../../../../ui/Float";
import { now } from "../../../../../dateUtils";
import Currency from "../../../../../uiControls/Currency";

interface BankEdiStorageKeys {
  modelStorage: string,
};

interface BankEdiViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
  jobName: JobNames
};

interface BankEdiLoadedDetailsViewParams {
  bankEdi: IGeneralWithList,
  bankEdiRef: MutableRefObject<IGeneralWithList|null>,
  setBankEdi: (x: Partial<IGeneralWithList>) => void,
  onPopupClose?: () => void,
  jobName: JobNames,
  tbId: string | undefined,
  appContext: IApplicationContext,
};

function getBankEdiStorageKeys(jobName: JobNames): BankEdiStorageKeys {
  return {modelStorage: `bill-payment-bank-edi-${jobName}`}
}

const newIGeneralWithList = {list: []};

function BankEdiImpl(params: BankEdiViewParams) {
  const {jobName, id} = params;
  const {modelStorage} = getBankEdiStorageKeys(jobName);

  const [inStorage, setInStorage, removeBankEdiFromStorage, bankEdiRef] = useTypedFastStorage<IGeneralWithList>(modelStorage, newIGeneralWithList);

  const appContext = useContext(ApplicationContext);

  useEffect(() => {
      initialize();
      return () => removeBankEdiFromStorage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    setInStorage({actualDateTo: now(), list: []})
  }

  async function initialize() {
    await loadExisting();
  }

  return (
    <BankEdiInitializedImpl
      bankEdi={inStorage!}
      bankEdiRef={bankEdiRef}
      setBankEdi={setInStorage}
      onPopupClose={params.onPopupClose}
      jobName={jobName}
      tbId={id}
      appContext={appContext}
    />
  );
}

function TypeRenderer({data, row}: any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`bank-edi-type-${row.index}`} maxLength={50}
             value={l.type} onChange={e => changeLineItem({type: e.target.value}, row.index)}/>
  );
}

function CheckNoRenderer({data, row}: any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`bank-edi-check-no-${row.index}`} maxLength={50}
             value={l.checkNo} onChange={e => changeLineItem({checkNo: e.target.value}, row.index)}/>
  );
}

function AmountRenderer({ data, row }: any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <Currency floatId={`bank-edi-amunt-${row.index}`} value={l.amount} onChange={amount => changeLineItem({ amount }, row.index)} 
      min={0} max={99999999.99} currency={'USD'}  />
  );
}

function RemarkRenderer({data, row}: any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`bank-edi-remark-${row.index}`} maxLength={50}
             value={l.remark} onChange={e => changeLineItem({remark: e.target.value}, row.index)}/>
  );
}

function BankEdiInitializedImpl({
                                  bankEdi,
                                  bankEdiRef,
                                  setBankEdi,
                                  onPopupClose,
                                  jobName,
                                  tbId,
                                  appContext,
                                }: BankEdiLoadedDetailsViewParams) {
  function onChange(change: Partial<IGeneralWithList>) {
    setBankEdi(change);
  }

  function toggleSelection(i: number) {
    const _list = _.cloneDeep(bankEdi.list);
    _list[i].selected = !_list[i].selected;

    onChange({list: _list})
  }

  function addLineItem() {
    const newInfo = { amount: 0, checkNo: '', type: '', remark: '' };

    return {
      setDataFunc: (list: any) => onChange({ list: list }),
      curListData: bankEdi.list,
      newData: newInfo
    };
  }

  function changeLineItem(change: any, index: number) {
    bankEdi.list[index] = _.merge(bankEdi.list[index], change);
    onChange({list: bankEdi.list});
  }

  function deleteRow(index: number) {
    if (!bankEdi.list) {
      return false;
    }

    let updated = _.cloneDeep(bankEdi.list);
    updated.splice(index, 1);

    onChange({ list: updated });
    return true;
  }

  const totalAmount = bankEdi.list.reduce((sum, item) => sum + item.amount, 0)

  function TotalLabelRenderer() {
    return <div className="ifms-input-ctr-row2-label"
                style={{textAlign: 'right', marginRight: '5px', marginTop: '10px'}}>
      Total
    </div>;
  }

  function TotalAmountRenderer() {
    return <div className='mt-2 ' style={{lineHeight: '15px'}}>
      <Float decimals={2} noborder disabled dark value={totalAmount}/></div>;
  }

  const tableOptions: ApplicableTableSettings = {
    tableName: 'bankEdiTable',
    ...bankEdiTable,
    useEditControls: true,
    data: bankEdi?.list.map((x, i) => ({ ...x, i, toggleSelection, changeLineItem, addLineItem })),
    rowRenderers: [
      { accessor: 'type', EditCell: TypeRenderer },
      { accessor: 'checkNo', EditCell: CheckNoRenderer },
      { accessor: 'amount', EditCell: AmountRenderer },
      { accessor: 'remark', EditCell: RemarkRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'type')
        return { type: TableCellTypes.Text, value: rowData.type };
      else if (accessor === 'checkNo')
        return { type: TableCellTypes.Text, value: rowData.checkNo };
      else if (accessor === 'amount')
        return { type: TableCellTypes.Number, value: rowData.amount, decimal: getDecimal('USD') };
      else if (accessor === 'remark')
        return { type: TableCellTypes.Text, value: rowData.remark };
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      return false;
    },

    footerRow: [
      {
        colspan: 2,
        renderer: TotalLabelRenderer
      },
      {
        colspan: 1,
        renderer: TotalAmountRenderer
      },
    ],
  };

  return (
    <div className='report-popup'> 
      <div style={{display: 'flex'}}>
        <div style={{width: '320px'}}>
          <InlineInputControlRow label="Actual Date" mode="smaller-2x">
            <div style={{display: 'flex'}}>
              <div style={{width: '80px'}}>
                <Date hideIcon value={bankEdi.actualDateFrom} onChange={actualDateFrom => onChange({actualDateFrom})}/>
              </div>

              <div style={{width: '50px', textAlign: 'center'}}>
                ~
              </div>

              <div style={{width: '80px'}}>
                <Date hideIcon value={bankEdi.actualDateTo} onChange={actualDateTo => onChange({actualDateTo})}/>
              </div>
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow label="Bank" mode='smaller-2x' smallMargin>
            <BankDropDown
              value={bankEdi.bank}
              onChange={bank => onChange({bank: bank?.id || 0, bankName: bank?.bankName})}
              autoSetDefault
            />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '40px', width: '320px'}}>
          <InlineInputControlRow label="EDI Type" mode="smaller-2x">
            <div style={{display: 'flex'}}>
              <div style={{width: '80px'}}>
                <TextBox value={bankEdi.ediType} onChange={e => onChange({ediType: e.target.value})}/>
              </div>

              <div style={{width: '70px', textAlign: 'right', paddingRight: '5px'}}>
                EDI Bank ID
              </div>

              <div style={{width: '60px'}}>
                <TextBox value={bankEdi.bankId} onChange={e => onChange({bankId: e.target.value})}/>
              </div>
            </div>
          </InlineInputControlRow>

          <InlineInputControlRow label='Bank Account #' mode='smaller-2x' smallMargin>
            <TextBox value={bankEdi.bankAccountNo} onChange={e => onChange({bankAccountNo: e.target.value})}/>
          </InlineInputControlRow>
        </div>
      </div>

      <div style={{display: 'flex', marginTop: '20px'}}>
        <div style={{width: '320px'}}>
          <InlineInputControlRow label="EDI Filename" mode='smaller-2x' smallMargin>
            <TextBox value={bankEdi.ediFilename} onChange={e => onChange({ediFilename: e.target.value})} />
          </InlineInputControlRow>
        </div>

        <div style={{marginLeft: '5px'}}>
          <SecondaryButton size='extrasmall'>Browse</SecondaryButton>
        </div>

        <div style={{margin: 'auto'}}></div>

        <div style={{marginRight: '20px'}}>
          <PrimaryButton size='extrasmall'>List</PrimaryButton>
        </div>
      </div>
      
      <div className='clearfix' />
      <div className='report-row mt-1'>
        <EditableTable
          tableOptions={tableOptions}
          deleteLine={deleteRow}
          addLine={addLineItem}
          getIndexOfFirstInvalidCell={() => undefined}
          showTotalLineNum={true}
        />
      </div>

      <div className='clearfix'/>
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          onClick={() => {

          }}>
          Create EDI
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const BankEdiDetails = ({
                          onPopupClose,
                          jobName,
                          selectedElementId
                        }: { onPopupClose?: () => void, jobName: JobNames, selectedElementId: number | null }) => {
  if (!selectedElementId) return null;

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <BankEdiImpl id={selectedElementId.toString()} jobName={jobName} onPopupClose={onPopupClose}
                 key={`bankEdi-details`}/>
  );
};
export default BankEdiDetails;
